import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {confirmAlert} from "react-confirm-alert";
import PageTitle from "../../../components/common/page-title/PageTitle";
import RedButton from "../../../components/common/buttons/RedButton";
import {FiCheckCircle, FiTrash, FiXCircle} from "react-icons/fi";
import LoadingOval from "../../../components/common/loading/LoadingOval";
import InsideContentForm from "../../../components/common/form/InsideContentForm";
import {Col, Container, Row} from "react-grid-system";
import LeftAlignLabelField from "../../../components/common/inputs/right-align-label-field/LeftAlignLabelField";
import TextInput from "../../../components/common/inputs/text-input/TextInput";
import Notice from "../../../components/common/notice/Notice";
import DividerFooter from "../../../components/common/footer/DividerFooter";
import InsideContentFormFooter from "../../../components/common/footer/InsideContentFormFooter";
import Button from "../../../components/common/buttons/Button";
import PrimaryButton from "../../../components/common/buttons/PrimaryButton";
import FileInput, {
    getBase64,
    imageTypes,
    categoryCoverType
} from "../../../components/common/inputs/file-input/FileInput";
import FileUploader from "../../../helpers/FileUploader";
import Category from "../../../classes/Category";

const EditPostCategory = () => {

    const params = useParams();
    const navigate = useNavigate();

    const [postCategory, setPostCategory] = useState(null);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    const [coverImageLocalFile, setCoverImageLocalFile] = useState(null);
    const [title, setTitle] = useState('');
    const [spanishTitle, setSpanishTitle] = useState('');
    const [order, setOrder] = useState(99);

    const handleSubmit = (event) => {
        event.preventDefault();
        savePostsCategory(postCategory, title, spanishTitle, coverImageLocalFile, (coverImageLocalFile != null));
    };

    useEffect(() => {

        if (params.id === undefined) {
            setLoading(false);
            return;
        }

        let isMounted = true;
        const controller = new AbortController();

        const fetchPostsCategory = async () => {
            try {
                setLoading(true);
                const postCategory = await Category.getAllQuery().get(params.id)

                if (isMounted) {
                    setPostCategory(postCategory);
                    setTitle(postCategory.getTitle());
                    setSpanishTitle(postCategory.getSpanishTitle());
                    setOrder(postCategory.getOrder());
                }
            }
            catch (error) {
                console.error(error);
            }
            finally {
                setLoading(false);
            }
        }

        fetchPostsCategory();

        return () => {
            isMounted = false;
            controller.abort();
        }
    }, []);

    const confirmDelete = (postsCategory) => {
        if (postsCategory != null) {
            confirmAlert({
                title: 'Delete posts category',
                message: 'This cannot be undone. Are you sure?',
                buttons: [
                    {
                        label: 'Yes',
                        onClick: () => deletePostsCategory(postsCategory)
                    },
                    {
                        label: 'No',
                    }
                ]
            });
        }
    }

    const deletePostsCategory = async (postsCategory) => {
        try {
            setSaving(true);
            setError(null);
            setSuccess(false);

            postsCategory.setEnabled(false);

            await postsCategory.save();

            navigate("/posts-categories");
        }
        catch (error) {
            console.error(error);
            setSuccess(false);
            setError(error);
        }
        finally {
            setSaving(false);
            if (postsCategory === null) {
                cleanValues();
            }
        }
    }

    function cleanValues() {
        setPostCategory(null);
        setTitle('');
        setSpanishTitle('');
        setCoverImageLocalFile(null);
    }


    const savePostsCategory = async (postsCategory, title, spanishTitle, file, fileWasChanged) => {
        try {
            setSaving(true);
            setError(null);
            setSuccess(false);

            let pPostsCategory = postsCategory

            if (postsCategory == null) {
                pPostsCategory = new Category();
            }

            pPostsCategory.setTitle(title);
            pPostsCategory.setSpanishTitle(spanishTitle);

            if (fileWasChanged) {
                const coverImageFile = await FileUploader.uploadFile(file, "posts-category-image-cover", file["type"]);
                pPostsCategory.setImageFile(coverImageFile);
            }

            pPostsCategory.setOrder(order);
            pPostsCategory.setPickable(true);
            pPostsCategory.setEnabled(true);
            await pPostsCategory.save();

            setSuccess(true);
        }
        catch (error) {
            console.error(error);
            setSuccess(false);
            setError(error);
        }
        finally {
            setSaving(false);
            if (postsCategory === null) {
                cleanValues();
            }
        }
    }

    const handleFileInputChange = (e) => {
        const file = e.target.files[0];

        getBase64(file)
            .then(result => {
                file["base64"] = result;
                setCoverImageLocalFile(file);
            })
            .catch(err => {
                console.error(err);
            });
    };

    return (
        <div className="PostsCategory">
            <PageTitle title="Posts category"
                       brief="Add a posts category" >
                { postCategory != null && <RedButton title="Delete" icon={<FiTrash/>} disabled={loading || saving} onClick={() => confirmDelete(postCategory)} /> }
                <LoadingOval loading={loading} />
            </PageTitle>
            <form onSubmit={handleSubmit}>
                { loading ? <></> :
                    <InsideContentForm>
                        <div className="Form__section-fields">
                            <Container fluid>
                                <Row gutterWidth={20}>
                                    <Col>
                                        <LeftAlignLabelField id="posts-category-cover-image"
                                                             label="Cover image">
                                            <FileInput name="posts-category-cover-image"
                                                       title="Select a cover image"
                                                       accept={imageTypes}
                                                       previewImage={
                                                           coverImageLocalFile !== null ?
                                                               coverImageLocalFile["base64"] :
                                                               postCategory !== null ?
                                                                   postCategory.getImageUrl() :
                                                                   null }
                                                       type={categoryCoverType}
                                                       onChange={handleFileInputChange}/>
                                        </LeftAlignLabelField>
                                    </Col>
                                </Row>
                                <Row gutterWidth={20}>
                                    <Col>
                                        <LeftAlignLabelField id="posts-category-title"
                                                             label="Title">
                                            <TextInput id="posts-category-title"
                                                       value={title}
                                                       required={true}
                                                       autoComplete={false}
                                                       onChange={(e) => setTitle(e.target.value)}/>
                                        </LeftAlignLabelField>
                                    </Col>
                                </Row>
                                <Row gutterWidth={20}>
                                    <Col>
                                        <LeftAlignLabelField id="posts-category-spanish-title"
                                                             label="Spanish title">
                                            <TextInput id="posts-category-spanish-title"
                                                       value={spanishTitle}
                                                       required={true}
                                                       autoComplete={false}
                                                       onChange={(e) => setSpanishTitle(e.target.value)}/>
                                        </LeftAlignLabelField>
                                    </Col>
                                </Row>
                            </Container>
                        </div>
                        { success &&
                            <Notice icon={<FiCheckCircle/>} type="success" fullWidth={true}>
                                <span>The posts category was saved successfully.</span>
                            </Notice>
                        }
                        { error !== null &&
                            <Notice icon={<FiXCircle />} type="error" fullWidth={true}>
                                { error.message }
                            </Notice>
                        }
                    </InsideContentForm>
                }
                <DividerFooter/>
                <InsideContentFormFooter>
                    <Button title="Cancel" onClick={() => navigate(-1)}/>
                    <PrimaryButton title="Save" type="submit" disabled={saving || loading }/>
                    {saving && <LoadingOval loading={saving}/>}
                </InsideContentFormFooter>
            </form>
        </div>
    );
};

export default EditPostCategory;